import { useEffect, useState } from "react";
import { Clock, Store } from "lucide-react";
import { useTheme } from "../hooks/useTheme.jsx";
import { serverNow } from "../utils/serverTime.js";
import { CLOSED_REASONS, formatCountdown, formatOpening, formatStoreTime } from "../utils/storeHours.js";
import { ImageWrapper } from "./ImageWrapper.jsx";
import { VersionTag } from "./VersionTag.jsx";
import defaultLogo from "../images/defaultLogo.svg";

// SOK-37: el horario (`schedule`) es el de la tienda y `closing_at` el fin de un evento; cada uno
// tiene su texto. El color de la barra está en index.css (.store-closing-bar).
const TEXTS = {
  [CLOSED_REASONS.HOURS]: { title: "Tienda cerrada", message: "En este momento no estamos recibiendo pedidos." },
  [CLOSED_REASONS.EVENT]: { title: "Evento finalizado", message: "¡Gracias por acompañarnos!" },
};

// Segundos que quedan hasta `closesAt`, con un reloj propio para no redibujar toda la app cada
// segundo.
function useCountdown(closesAt) {
  const [remaining, setRemaining] = useState(() => (closesAt ? closesAt.getTime() - serverNow().getTime() : 0));

  useEffect(() => {
    if (!closesAt) return undefined;
    const tick = () => setRemaining(closesAt.getTime() - serverNow().getTime());
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [closesAt]);

  return remaining;
}

/**
 * Franja de color arriba de la pantalla que empuja la app hacia abajo (ver
 * `:root[data-store-closing]` en index.css): cuenta regresiva antes del cierre o, si el local ya
 * cerró con una compra en curso, el aviso de que solo queda terminarla.
 */
export function StoreClosingBar({ closesAt, closed, timeZone }) {
  const remaining = useCountdown(closed ? null : closesAt);

  return (
    <div className="store-closing-bar" role="status" aria-live="polite">
      <Clock aria-hidden="true" className="store-closing-bar-icon shrink-0" strokeWidth={2.5} />
      {closed ? (
        <p className="store-closing-bar-text secondary-font-bold">La tienda ya cerró. Puedes terminar tu compra.</p>
      ) : (
        <p className="store-closing-bar-text secondary-font-bold">
          La tienda cierra a las {formatStoreTime(closesAt, timeZone)}
          <span className="store-closing-bar-countdown">{formatCountdown(remaining)}</span>
        </p>
      )}
    </div>
  );
}

/**
 * Pantalla del local cerrado. A diferencia de la de "No disponible", deja a la vista el logo y la
 * versión, que son la secuencia que abre el menú secreto (cierre de caja y demás).
 */
export function StoreClosedScreen({ status, style, version, connectivity, onLogoClick, onVersionClick }) {
  const { theme } = useTheme();
  const logo = theme === "dark"
    ? (style?.global?.secondaryClientLogo || style?.global?.mainClientLogo || defaultLogo)
    : (style?.global?.mainClientLogo || style?.global?.secondaryClientLogo || defaultLogo);
  const clientName = style?.global?.clientName || "Logo Cliente";
  const { title, message } = TEXTS[status.reason] || TEXTS[CLOSED_REASONS.HOURS];
  // "hoy" o "mañana" cambian a medianoche aunque el estado del local no cambie.
  const [now, setNow] = useState(serverNow);
  useEffect(() => {
    const timer = setInterval(() => setNow(serverNow()), 60000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="store-closed-screen bg-back text-[var(--color-text)]">
      <div className="h-40 w-full max-w-[600px] cursor-pointer" onClick={onLogoClick}>
        <ImageWrapper
          image={logo}
          alt={clientName}
          display="block"
          mode="contain"
          width="100%"
          height="100%"
          backgroundColor="transparent"
          margin="0"
          padding="0"
          borderRadius="0"
          transition={true}
          loadDelay={0.1}
        />
      </div>

      <div className="flex flex-col items-center gap-6 text-center">
        <Store aria-hidden="true" className="w-28 h-28" strokeWidth={1.75} />
        <h1 className="secondary-font-bold uppercase fs-56 lh-64">{title}</h1>
        <p className="secondary-font-normal fs-32 lh-40 max-w-[800px]">{message}</p>
        {status.opensAt ? (
          <p className="secondary-font-bold fs-36 lh-44">
            Abrimos {formatOpening(status.opensAt, now, status.timeZone)}
          </p>
        ) : null}
      </div>

      <div className="store-closed-version">
        <VersionTag version={version} connectivity={connectivity} onClick={onVersionClick} />
      </div>
    </div>
  );
}
